//@author: devin

import { NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    input,
    NgZone,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges,
    TemplateRef,
    ViewEncapsulation,
} from "@angular/core";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25EditableAbstract } from "../s25.editable.abstract";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";

@TypeManagerDecorator("s25-ng-editable-text")
@Component({
    selector: "s25-ng-editable-text",
    template: `
        @if (this.readOnly) {
            <span class="s25-data">{{ this.val }}</span>
        } @else {
            @if (!this.editing) {
                <div
                    class="s25-editable-shared-container c-editable-text editable editable-click"
                    (click)="editMode($event)"
                    (keyup.enter)="editMode($event)"
                    (keydown.space)="editMode($event)"
                    tabindex="0"
                >
                    <ng-container
                        [ngTemplateOutlet]="readonlyTemplate() ?? defaultReadonlyTemplate"
                        [ngTemplateOutletContext]="{ $implicit: this.val }"
                    ></ng-container>
                </div>
            } @else {
                <input
                    class="firstFocusEditable c-input"
                    type="text"
                    [id]="this.fieldId"
                    [minlength]="this.min"
                    [maxlength]="this.max"
                    [disabled]="this.disabled"
                    [ngModel]="this.candidateVal"
                    (ngModelChange)="this.update($event)"
                    (blur)="this.onInputBlur()"
                    (keyup.enter)="this.submit()"
                />
                <div class="aw-button-group">
                    @if (this.hasCancelButton) {
                        <button
                            class="aw-button aw-button--outline"
                            (click)="this.cancel()"
                            (keyup.enter)="this.cancel()"
                        >
                            {{ this.cancelButtonText }}
                        </button>
                    }
                    @if (this.hasCommitButton) {
                        <button
                            class="aw-button aw-button--primary"
                            (click)="this.commit()"
                            (keyup.enter)="this.commit()"
                            [disabled]="committing"
                            [class.is-committing]="committing"
                        >
                            {{ committing ? "" : commitButtonText }}
                            <s25-loading-inline [model]="{}"></s25-loading-inline>
                        </button>
                    }
                </div>
                @if (this.errorMessages) {
                    <div>
                        @for (msg of this.errorMessages; track msg) {
                            <div class="ngRed ngBold">
                                {{ msg }}
                            </div>
                        }
                    </div>
                }
            }
        }
        <ng-template #defaultReadonlyTemplate let-val>
            <div class="s25-editable s25-data">{{ val }}</div>
        </ng-template>
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    host: {
        "[class.ng-editable-text]": "true",
        "[class.ng-editable-text--weak-edit]": "weakEditButton()",
    },
    styles: `
        .ng-editable-text--weak-edit:host {
            .s25-editable-shared-container {
                &:not(:hover, :focus, :active):before {
                    opacity: 0;
                }

                &:before {
                    transition: opacity 0.2s ease-out;
                }
            }
        }

        .s25-data {
            overflow-wrap: break-word;
        }
    `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => S25EditableTextComponent), multi: true }],
    imports: [NgTemplateOutlet, FormsModule, S25LoadingInlineModule],
})
export class S25EditableTextComponent extends S25EditableAbstract<string> implements OnInit, OnChanges {
    public readonly readonlyTemplate = input<TemplateRef<string> | undefined>();
    public readonly weakEditButton = input<boolean>(false);

    getType = () => "text";

    constructor(elementRef: ElementRef, cd: ChangeDetectorRef, zone: NgZone, renderer: Renderer2) {
        super(elementRef, cd, zone, renderer);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!changes["val"]?.isFirstChange()) {
            this.valChange.emit(this.val);
        }
    }
}
